/* istanbul ignore file */
import { Alert, Modal } from '@distribusion/owl-frontend-ui'
import { Formik } from 'formik'
import React, { useState } from 'react'

import LoginForm from '@components/LoginModal/Form'
import { useUserAuth } from '@hooks/userAuthProvider/provider'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { useEmailChange } from '@pages/Account/hooks/useEmailChange'
import { usePasswordChange } from '@pages/Account/hooks/usePasswordChange'
import { ConfirmationModal, ForgotPasswordModal, ErrorModal } from '@pages/Account/Settings/Modals'

import '@components/LoginModal/index.scss'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
}

export interface LoginFormState {
  email: string
  password: string
}

export const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
  const { t } = useTranslation()
  const [showAlert, setShowAlert] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const { createSession } = useUserAuth()
  const { isConfirmModalOpen, closeConfirmModal, handleForgotPasswordSuccess, newEmail, confirmationType } =
    usePasswordChange()

  const { openErrorModal, isErrorModalOpen, closeErrorModal, errorType } = useEmailChange()

  const handleSubmit = async (values: LoginFormState): Promise<void> => {
    try {
      await createSession({
        email: values.email,
        password: values.password,
      })

      onClose()
    } catch (error) {
      setShowAlert(true)
    }
  }

  const handleForgotPasswordOpen = () => {
    onClose()
    setShowForgotPassword(true)
  }

  const handleForgotPasswordClose = () => {
    setShowForgotPassword(false)
  }

  const handleForgotPasswordSuccessLocal = (email: string) => {
    setShowForgotPassword(false)
    handleForgotPasswordSuccess(email)
  }

  const handleRetryErrorModal = () => {
    closeErrorModal()
    setShowForgotPassword(true)
  }

  return (
    <>
      <Formik validateOnBlur initialValues={{ email: '', password: '' }} onSubmit={handleSubmit}>
        <Modal
          className="login-modal"
          maxWidth="sm"
          opened={isOpen}
          onClose={onClose}
          title={<h3 className={bem('login-modal', 'title')}>{t('subscription.loginModal.title')}</h3>}
        >
          {showAlert && (
            <Alert
              message={t('subscription.loginModal.alert')}
              type="error"
              onClose={() => setShowAlert(false)}
              variant="compact"
            />
          )}
          <LoginForm onForgotPasswordClick={handleForgotPasswordOpen} />
        </Modal>
      </Formik>
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={handleForgotPasswordClose}
        onSuccess={handleForgotPasswordSuccessLocal}
        onError={openErrorModal}
      />
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={closeConfirmModal}
        email={newEmail}
        type={confirmationType}
      />
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={closeErrorModal}
        errorType={errorType}
        onRetry={handleRetryErrorModal}
      />
    </>
  )
}
